import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react'
import { normalizeReportDateParameter } from './reportDateParameter.util'
import { getReportPrintPageCss } from './reportPrintPage.util'
import { printRenderedReport } from './reportPrintFrame.util'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { HeaderArea } from './HeaderArea'
import { Row, Button, Card, Spinner } from 'react-bootstrap'
import { PrintArea } from './PrintArea'
import FeatherIcon from 'feather-icons-react'
import httpClient from 'utility/httpClient.util'
import FileSaver from 'file-saver'
import './CreateReport.scss'
import './PrintReport.scss'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import _ from 'lodash'
import moment from 'moment'
import dot from 'dot-object'
import PdfIcon from 'assets/icons/pdf.png'
import PreviewFile from 'components/Document/PreviewFile'
import useAuth from 'hooks/useAuth'
import Select, { components } from "react-select";
import {languageOptionWithFlag} from "./CreateReport"
import {CustomSingleValue,IconOption} from "./PublishMenu"
import { getAttachmentFileUrl, getAttachmentId } from './resourceUpload'
import { getReportBindValue } from './reportBind.util'
import {
  isReportItemVisibleOnLayoutPage,
  normalizeReportResourcePublishData,
} from './reportResource.util'
import { isPdfOverlayOutputMode } from './reportOutputMode.util'
import {
  clearHiddenReportParameterValues,
  isReportParameterRequired,
  isReportParameterVisible,
} from './reportParameterCondition.util'
import { calculateReportRepeatTotalPage } from './reportPagination.util'
import { paginateReportGroups } from './reportGroupPagination.util'
import { getPdfOverlayPreviewErrorMessage } from './pdfOverlayPreviewError.util'
import ReportPrintSettings from 'components/Report/ReportPrintSettings'
import {
  isPrintIntent,
  prepareReportWithPrintConfig,
  reportPdfRenderQuery,
} from 'utility/reportPrintConfig.util'
import { pickDesktopPrintSource } from 'utility/desktopPrintSource.util'
import FlowPrintLayout from './FlowPrintLayout'
import { hasReportFlow } from './reportFlow.util'
const DEFAULT_BOX = {
  top: 0,
  left: 0,
  title: '',
  itemType: 'text',
  fieldName: '',
  defaultValue: '',
  value: '',
  type: 'text',
}

const DEFAULT_QUERY = {}

const getPdfReportResource = (printData = {}) =>
  Object.values(printData || {}).find(
    (item) =>
      item?.type === 'pdf' && (getAttachmentId(item) || getAttachmentId(item?.pdf)),
  )

const getPdfReportAttachmentId = (item) =>
  getAttachmentId(item) || getAttachmentId(item?.pdf)

const getPdfReportFileUrl = (item) =>
  item?.previewUrl || getAttachmentFileUrl(getPdfReportAttachmentId(item))

const sanitizePrintDocumentTitle = (value = '') =>
  String(value || '')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const getPrintDocumentTitle = (report = {}) =>
  sanitizePrintDocumentTitle(report?.reportName) || 'report'

const setPrintDocumentTitle = (report = {}) => {
  if (typeof document === 'undefined') return
  document.title = getPrintDocumentTitle(report)
}

const getExcelDownloadFileName = (response, fallbackName) => {
  const disposition = response?.headers?.['content-disposition'] || ''
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  const plainName = disposition.match(/filename="?([^";]+)"?/i)?.[1]

  if (encodedName) {
    try {
      return decodeURIComponent(encodedName)
    } catch (error) {
      console.error('Unable to decode Excel filename', error)
    }
  }

  return plainName || `${fallbackName || 'report'}.xlsx`
}

const getDataSourceVariableName = (item = {}) =>
  `${item.dataSourceVariableName || ''}`.trim()

const normalizeReportParamName = (value = '') =>
  `${value || ''}`.trim().replace(/^\{\{/, '').replace(/\}\}$/, '').trim()

const hasReportParamValue = (value) => {
  if (Array.isArray(value)) return value.length > 0
  if (value === null || typeof value === 'undefined') return false
  if (typeof value === 'string') return value.trim() !== ''

  return true
}

const getOptionalParamNames = (dataSource = {}) => {
  const optionalBlocks = dataSource?.params?.optionalBlocks || []
  const names = optionalBlocks
    .map((item) => normalizeReportParamName(item?.param))
    .filter(Boolean)
  const query = dataSource?.query || ''
  const regex = /{{#optional\s+([A-Za-z0-9_.-]+)\s*}}/g
  let match = regex.exec(query)

  while (match) {
    names.push(normalizeReportParamName(match[1]))
    match = regex.exec(query)
  }

  return _.uniq(names)
}

const getTextEditorRepeatVariables = (sourceCode = '') => {
  if (!sourceCode) return []

  if (typeof document === 'undefined') {
    const variables = []
    const repeatRegex = /<tr\b[^>]*\bdata-repeat=["']([^"']+)["'][^>]*>/gi
    let match = repeatRegex.exec(sourceCode)

    while (match) {
      variables.push(match[1])
      match = repeatRegex.exec(sourceCode)
    }

    return _.uniq(variables.filter(Boolean))
  }

  const template = document.createElement('template')
  template.innerHTML = sourceCode

  return _.uniq(
    Array.from(template.content.querySelectorAll('tr[data-repeat]'))
      .map((row) => `${row.getAttribute('data-repeat') || ''}`.trim())
      .filter(Boolean),
  )
}

const calculateTextEditorRepeatTotalPage = (item = {}, dataSourceList = []) => {
  const repeatVariables = getTextEditorRepeatVariables(item.sourceCode)
  if (!repeatVariables.length) return 1

  const dataSourceMap = (dataSourceList || []).reduce((result, dataSource) => {
    const variableName = getDataSourceVariableName(dataSource)
    if (variableName) result[variableName] = dataSource

    return result
  }, {})

  const maxRepeatRows = repeatVariables.reduce((maxRows, variableName) => {
    const rows = dataSourceMap[variableName]?.fieldVariable?.value
    if (!Array.isArray(rows)) return maxRows

    return Math.max(maxRows, rows.length)
  }, 0)

  return calculateReportRepeatTotalPage({
    rowCount: maxRepeatRows,
    rowPerPage: item.rowPerPage,
    pageBreakAfterRows: item.pageBreakAfterRows,
    lastPageRowPerPage: item.lastPageRowPerPage,
    rowsOnFirstPageOnly: item.rowsOnFirstPageOnly === true,
  })
}

const PrintReport = (props) => {
  const { user } = useAuth()
  const [debug, setDebug] = useState(false)
  const [totalPage, setTotalPage] = useState(1)
  const [totalInPageIndex, setTotalInPageIndex] = useState({ 0: 1 })
  const [layout, setLayout] = useState({
    size: 'A4',
    //orientation: 'portrait',
    orientation: 'landscape',
    //width: '21cm',
    width: '29.7cm',
    height: '21cm',
    paddingLeft: '0.5in',
    paddingRight: '0.5in',
    paddingTop: '0.5in',
    paddingBottom: '0.5in',
  })
  const [displayType, setDisplayType] = useState('web')
  const [fileUrl, setFileUrl] = useState(null)
  const [blobFile, setBlobFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [pdfPrintSignal, setPdfPrintSignal] = useState(0)
  const [pdfOverlayPreviewStatus, setPdfOverlayPreviewStatus] = useState('idle')
  const [isPrintPreparing, setIsPrintPreparing] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)

  const [pdfSrc, setPdfSrc] = useState('')
  const [height, setHeight] = useState(window.innerHeight - 100)
  const [query, setQuery] = useState({})
  const [inputParam, setInputParam] = useState('')
  const [loading, setLoading] = useState(true)
  const [showOnlyPrintArea, setShowOnlyPrintArea] = useState(false)
  const printPageRef = useRef(null)
  const printFrameCleanupRef = useRef(null)
  useEffect(() => () => printFrameCleanupRef.current?.(), [])
  const desktopPrintRef = useRef(null)
  const printIntentConsumedRef = useRef(false)
  const [initialReportReady, setInitialReportReady] = useState(false)
  const [reportInputsReady, setReportInputsReady] = useState(false)
  const [report, setReport] = useState({
    reportName: '',
    publishData: {
      printStyle: false,
      pdfStickerStyle: false,
      exportExcel: false,
      autoPrint: false,
      rotateOnPrint: false,
      langReportCode:{},
      languageSupport:''
    },
  })
  const [boxes, setBoxes] = useState({
    // a: Object.assign({}, DEFAULT_BOX, {
    //   id: 'a',
    //   top: 20,
    //   left: 80,
    //   value: 'Drag me around',
    //   type: 'date',
    // }),
    // b: Object.assign({}, DEFAULT_BOX, {
    //   id: 'b',
    //   top: 180,
    //   left: 20,
    //   value: 'Drag me too',
    //   type: 'image',
    //   width: '128',
    //   height: '128',
    // }),
    0: Object.assign({}, DEFAULT_BOX, {
      id: 'b',
      top: 0,
      left: 0,
      value: 'Loading...',
      type: 'text',
    }),
  })
  const [inputs, setInputs] = useState([])
  const [loadingDataSourceIds, setLoadingDataSourceIds] = useState([])
  const dropdownOptionsTimerRef = useRef(null)
  const dropdownOptionsRequestRef = useRef(0)

  useEffect(() => () => {
    clearTimeout(dropdownOptionsTimerRef.current)
    dropdownOptionsRequestRef.current += 1
  }, [])
  const flowEnabled = hasReportFlow(boxes)
  const [flowStatus, setFlowStatus] = useState({ ready: false, error: '' })
  const flowStatusRef = useRef({ ready: false })
  const onFlowStatus = useCallback((status) => {
    flowStatusRef.current = status
    setFlowStatus(status)
    if (status.ready) setTotalPage(status.pages)
  }, [])
  useEffect(() => {
    const shouldAutoPrint = showOnlyPrintArea
      ? query.browserPrint === 'true'
      : query.printIntent !== 'preview' &&
        (report.desktopPrintConfig?.autoPrint === true || isPrintIntent(query))
    if (!initialReportReady || !reportInputsReady || loading || isLoading ||
      !shouldAutoPrint || printIntentConsumedRef.current ||
      (flowEnabled && !flowStatus.ready) ||
      (displayType === 'pdfAttachment' && !fileUrl) ||
      (report.publishData.pdfStickerStyle && !pdfSrc)) return
    printIntentConsumedRef.current = true
    // A saved machine preference prints the first complete report on opening.
    // Server rendering and explicitly requested previews never submit a job.
    if (showOnlyPrintArea) browserPrint()
    else desktopPrintRef.current?.printConfigured({
      device: report.desktopPrintDevice,
      printConfig: report.desktopPrintConfig,
    })
  }, [initialReportReady, reportInputsReady, loading, isLoading, showOnlyPrintArea, query,
    flowEnabled, flowStatus.ready, displayType, fileUrl, pdfSrc, report])

  useLayoutEffect(() => {
    const style = document.createElement('style')
    style.setAttribute('data-report-page-style', 'true')
    style.media = 'print'
    style.textContent = getReportPrintPageCss(layout, showOnlyPrintArea)
    document.head.appendChild(style)
    return () => style.remove()
  }, [layout, showOnlyPrintArea])

  const hideSplash = () => {
    const element = document.getElementById('public-splash-screen')
    if (element) element.style.display = 'none'
  }

  //const useSearch = useLocation().search
  useEffect(() => {
    let interval

    if (isLoading) {
      // Start the timer by setting up an interval that increments the seconds state every 1000ms (1 second)
      interval = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds + 1)
      }, 1000)
    } else {
      // Once loading is complete, clear the interval and optionally reset the counter
      clearInterval(interval)
      setSeconds(0) // Uncomment this line if you want to reset the counter after loading
    }

    // Cleanup function to clear the interval when the component unmounts or when loading stops
    return () => clearInterval(interval)
  }, [isLoading]) // Effect depends on the isLoading prop

  useEffect(() => {
    // Check if activeFile.id is available
    if (displayType !== 'pdf') {
      if (fileUrl?.startsWith('blob:')) URL.revokeObjectURL(fileUrl)
      setBlobFile(null)
      if (displayType !== 'pdfAttachment') setFileUrl(null)
      return // If not, exit the effect
    }

    setIsLoading(true)
    let appURL = `${window.location.protocol}//${window.location.host}`
    if (process.env.NODE_ENV == 'development') {
      appURL = 'https://dev.ckd.ckhis.xyz'
    }

    httpClient({
      method: 'post',
      url: `/report/report-pdf/export-pdf`, // Adjust URL to include the activeFile.id
      data: {
        url: `${appURL}/#/print/report?${reportPdfRenderQuery(inputParam)}`,
      },
      responseType: 'blob',
    })
      .then((response) => {
        // Revoke the previous URL to avoid memory leaks
        if (fileUrl) {
          URL.revokeObjectURL(fileUrl)
        }
        const blobFile = new Blob([response.data], { type: 'application/pdf' })
        const url = URL.createObjectURL(blobFile)
        setFileUrl(url)
        setBlobFile(blobFile)
      })
      .catch((error) => {
        console.error('Error fetching the PDF:', error)
        setFileUrl(null)
      })
      .finally(() => {
        setIsLoading(false)
      })

    // Cleanup function
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl)
        setBlobFile(null)
      }
    }
  }, [displayType]) // Dependency array now includes activeFile.id

  useEffect(() => {
    hideSplash()
    const handleResize = () => {
      setHeight(window.innerHeight - 100)
    }

    // Set up event listener to update height when window size changes
    window.addEventListener('resize', handleResize)

    // Clean up event listener when component unmounts
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const previousTitle = document.title
    setPrintDocumentTitle(report)

    return () => {
      document.title = previousTitle
    }
  }, [report.reportName])

  useEffect(() => {
    let cancelled = false
    const initializeReport = async () => {
      //const query = new URLSearchParams(useSearch)
      const hash = window.location.hash
      // Extract the query string part from the hash. This depends on the structure of your URLs.
      const queryString = hash.substring(hash.indexOf('?'))
      const query = new URLSearchParams(queryString)
      //console.log('qu',query.keys(),query.get('reportCode'),window.location)
      if (query.get('debug')) {
        const flag = query.get('debug') == 'true'
        setDebug(flag)
      }
      const showOnlyPrintArea =
        query.get('showOnlyPrintArea') && query.get('showOnlyPrintArea') == 'true'
      setShowOnlyPrintArea(showOnlyPrintArea)
      if (props?.showOnlyPrintArea && props?.showOnlyPrintArea === true) {
        setShowOnlyPrintArea(props.showOnlyPrintArea)
      }
      if (
        query.get('token') &&
        query.get('token') != '' &&
        query.get('tokenType')
      ) {
        localStorage.setItem('tokenType', query.get('tokenType'))
        localStorage.setItem('token', query.get('token'))
      }

      let param = ''
      let keys = {}
      for (var key of query.keys()) {
        let val = query.get(key)
        let newVal = key === 'vn' ? props?.vn ?? query.get(key) : query.get(key)
        if (val) {
          param += '&' + key + '=' + newVal
          keys[key] = val
        }
      }

      //console.log('query',query,props)
      Object.entries(props).forEach(([key, value]) => {
        if (
          [
            'history',
            'location',
            'match',
            'staticContext',
            'showInput',
            'onClickPrint',
            'printOnNewTab',
          ].includes(key)
        ) {
          return
        }
        param += '&' + key + '=' + value
        keys[key] = value
      })
      setInitialReportReady(false)
      setReportInputsReady(false)
      printIntentConsumedRef.current = false
      setQuery({ ...DEFAULT_QUERY, ...keys })
      setInputParam(param)
      let cfg = {}
      if (props?.jsonData) {
        cfg = await parseSearchResult(props.jsonData, keys)
        setReportInputsReady(areReportInputsReady(cfg, keys))
      } else {
        cfg = await searchByInput(param, keys)
      }

      if (cancelled || (!cfg.reportTemplateId && !props?.jsonData)) return
      let newLayout = Object.assign({}, layout, cfg.layoutData)

      setLayout(newLayout)

      if (isPdfOverlayOutputMode(cfg.publishData)) {
        await generatePdfOverlay(param)
      } else if (
        cfg.publishData.pdfStickerStyle &&
        cfg.publishData.pdfStickerStyle === true
      ) {
        await generatePdf(param, keys)
      }
      if (!cancelled) setInitialReportReady(true)
    }
    initializeReport().catch((error) => {
      console.error('Unable to initialize report', error)
      if (!cancelled) { setLoading(false); setInitialReportReady(false) }
    })
    return () => { cancelled = true }
  }, [JSON.stringify(props)])

  const deduplicateQueryParams = (url) => {
    const [baseUrl, queryString] = url.split('?')
    if (!queryString) return url

    // Parse the query string into an array of [key, value] pairs
    const paramsArray = queryString.split('&').map((pair) => pair.split('='))

    // Use _.fromPairs to create an object (automatically deduplicates by keeping the last key-value pair)
    const uniqueParamsObject = _.fromPairs(paramsArray)

    // Reconstruct the query string from the deduplicated object
    const deduplicatedQueryString = _.toPairs(uniqueParamsObject)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')

    return `${baseUrl}?${deduplicatedQueryString}`
  }

  const buildPrintReportUrl = (extraParams = {}) => {
    let url = `/#/print/report?_code=printOnNewTab${inputParam}`

    Object.entries(extraParams).forEach(([key, value]) => {
      if (value === undefined || value === null || value === false) return
      url += `&${key}=${value}`
    })

    return deduplicateQueryParams(url)
  }

  const printWebReportInFrame = () => {
    printFrameCleanupRef.current?.()
    setIsPrintPreparing(true)
    printFrameCleanupRef.current = printRenderedReport({
      source: printPageRef.current,
      layout,
      onPrepared: () => setIsPrintPreparing(false),
      onError: (error) => {
        setIsPrintPreparing(false)
        Swal.fire({
          icon: 'error',
          title: 'เตรียมหน้าพิมพ์ไม่สำเร็จ',
          text: error.message,
        })
      },
    })
  }

  const printPdfBlobUrl = (url) => {
    if (!url) return

    setIsPrintPreparing(true)

    const iframe = document.createElement('iframe')
    let cleanupTimer = null
    let preparingTimer = null
    let cleaned = false

    const finishPreparing = () => {
      setIsPrintPreparing(false)
    }

    const handleWindowFocus = () => {
      finishPreparing()
    }

    const cleanup = () => {
      if (cleaned) return
      cleaned = true
      finishPreparing()
      window.clearTimeout(cleanupTimer)
      window.clearTimeout(preparingTimer)
      window.removeEventListener('focus', handleWindowFocus)
      setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe)
        }
      }, 1000)
    }

    const fallbackOpenPdf = (error) => {
      console.error('Unable to open PDF print dialog', error)
      cleanup()
      window.open(url, '_blank')
    }

    iframe.title = 'sticker-pdf-print-frame'
    iframe.style.position = 'fixed'
    iframe.style.left = '-10000px'
    iframe.style.top = '0'
    iframe.style.width = '1px'
    iframe.style.height = '1px'
    iframe.style.border = '0'
    iframe.style.opacity = '0'
    iframe.style.pointerEvents = 'none'

    iframe.onload = () => {
      try {
        const printWindow = iframe.contentWindow
        if (!printWindow) return

        printWindow.onafterprint = cleanup
        printWindow.addEventListener('afterprint', cleanup, { once: true })
        window.addEventListener('focus', handleWindowFocus, { once: true })
        setTimeout(() => {
          printWindow.focus()
          printWindow.print()
          preparingTimer = window.setTimeout(finishPreparing, 800)
        }, 250)
      } catch (error) {
        fallbackOpenPdf(error)
      }
    }

    iframe.onerror = fallbackOpenPdf
    iframe.src = url
    document.body.appendChild(iframe)
    cleanupTimer = window.setTimeout(cleanup, 30000)
  }

  const searchByInput = async (param, keys) => {
    setReportInputsReady(false)
    flowStatusRef.current = { ready: false }
    setFlowStatus({ ready: false, error: '' })
    setLoading(true)
    const cleanedUrl = deduplicateQueryParams(
      '/report/report-webview/find?convertQuery=true&include=reportTemplate' +
        param,
    )
    const cfg = await prepareReportWithPrintConfig(cleanedUrl, {
      rendering: keys?.showOnlyPrintArea === 'true' || keys?.showOnlyPrintArea === true,
    })
      .catch(() => {
        withReactContent(Swal).fire({
          icon: 'error',
          title: 'Error',
          text: 'พบปัญหาการพิมพ์ กรุณาติดต่อแอดมิน',
        })
        return {
          reportName: '',
          publishData: {
            printStyle: false,
            pdfStickerStyle: false,
            rotateOnPrint: false,
            exportExcel: false,
            autoPrint: false,
          },
          dataSourceData: { list: [] },
          paramsData: { inputs: [] },
        }
      })
    const permissionCheck = cfg?.publishData?.permissions || []
    const userGroup = user?.groups[0]
    if (permissionCheck?.length > 0) {
      let groups = permissionCheck?.map((item) => item.name).join(', ')

      const hasPermission = permissionCheck?.some(
        (perm) => perm.path === userGroup,
      )
      if (!hasPermission) {
        Swal.fire({
          title: 'ไม่มี Permission',
          icon: 'error',
          text: `Permission Deny for Group [${groups}] 
          Please contact Admin to grant this.! `,
          confirmButtonText: 'OK',
        }).then(() => {
          window.close() // ปิดหน้าต่าง
        })
        setLoading(false)
        return getInitReport()
      }
    }
    setLoading(false)
    const parsedReport = await parseSearchResult(cfg, keys)
    setReportInputsReady(areReportInputsReady(parsedReport, keys))
    return parsedReport
  }

  const generatePdf = async (param, keys) => {
    setLoading(true)
    const cleanedUrl = deduplicateQueryParams(
      '/report/report-webview/sticker-pdf?convertQuery=true' + param,
    )
    const response = await httpClient.get(cleanedUrl, {
      responseType: 'blob', // Important
    })

    // Create a blob URL
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    setPdfSrc(url)
    setLoading(false)
  }

  const generatePdfOverlay = async (param) => {
    setLoading(true)
    setPdfOverlayPreviewStatus('loading')
    const cleanedUrl = deduplicateQueryParams(
      '/report/report-webview/pdf-overlay?convertQuery=true' + param,
    )

    try {
      const response = await httpClient.get(cleanedUrl, {
        responseType: 'blob',
      })
      const blobFile = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blobFile)

      if (fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl)
      }
      setDisplayType('pdfAttachment')
      setFileUrl(url)
      setBlobFile(blobFile)
      setPdfPrintSignal(0)
      setTotalPage(1)
      setTotalInPageIndex({ 0: 1 })
      setPdfOverlayPreviewStatus('ready')
    } catch (error) {
      console.error('Unable to generate PDF overlay preview', error)
      setFileUrl(null)
      setPdfOverlayPreviewStatus('error')
      const errorMessage = await getPdfOverlayPreviewErrorMessage(error)
      await Swal.fire({
        icon: 'error',
        title: 'Preview ไม่สำเร็จ',
        text: errorMessage,
      })
    } finally {
      setLoading(false)
    }
  }

  const isIsoDate = (str) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false
    var d = new Date(str)
    return d.toISOString() === str
  }

  const getFromApi = async (endpoint) => {
    const resultData = await httpClient
      .get(endpoint)
      .then(({ data }) => data)
      .catch(() => {
        withReactContent(Swal).fire({
          icon: 'error',
          title: 'Error',
          text: 'ไม่สามารถ Get API จาก Endpoint ได้',
        })
        throw new Error('ไม่สามารถโหลดข้อมูลเพิ่มเติมของรายงานได้')
      })
    return await resultData
  }

  const buildParameterInputList = (cfg, keys) => {
    let list = []
    const paramsData = cfg.paramsData?.inputs || []

    cfg.dataSourceData?.list?.forEach((element) => {
      const optionalParamNames = getOptionalParamNames(element)
      const dataSourceInputs = element.params?.inputs || []

      dataSourceInputs.forEach((item) => {
        const key = normalizeReportParamName(item)
        const isOptional = optionalParamNames.includes(key)
        const find = paramsData.find(({ title }) => title === key) || {}

        if (find?.dropdownData?.dataSourceId) {
          const findChoice = cfg.dataSourceData.list.find(
            ({ dataSourceId }) =>
              dataSourceId === find.dropdownData.dataSourceId,
          )

          if (findChoice && find.dropdownData) {
            find.dropdownData.choices = findChoice.fieldVariable?.value || []
          }
        }

        const paramInput = {
          ...find,
          title: key,
          value: hasReportParamValue(keys[key]) ? keys[key] : '',
          field: key,
          optional: isOptional,
        }
        const existingIndex = list.findIndex(({ field }) => field === key)

        if (existingIndex >= 0) {
          list[existingIndex] = {
            ...list[existingIndex],
            ...paramInput,
            optional: list[existingIndex].optional && isOptional,
          }
        } else {
          list.push(paramInput)
        }
      })
    })

    return _.uniqBy(list, 'field')
  }

  const areReportInputsReady = (cfg, keys) => {
    const loadedInputs = clearHiddenReportParameterValues(buildParameterInputList(cfg, keys))
    return loadedInputs.every((item) =>
      !isReportParameterRequired(item, loadedInputs) || hasReportParamValue(item.value),
    )
  }

  const parseSearchResult = async (cfg, keys) => {
    cfg = Object.assign(getInitReport(), cfg)
    cfg.publishData = normalizeReportResourcePublishData(cfg.publishData)
    const pdfOverlayMode = isPdfOverlayOutputMode(cfg.publishData)
    setPdfOverlayPreviewStatus(pdfOverlayMode ? 'loading' : 'idle')
    setReport(cfg)
    setPrintDocumentTitle(cfg)

    if (pdfOverlayMode) {
      if (fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl)
      }
      setDisplayType('pdfAttachment')
      setFileUrl(null)
      setBlobFile(null)
      setPdfPrintSignal(0)
      setTotalPage(1)
      setTotalInPageIndex({ 0: 1 })
      setBoxes(cfg.printData || {})
      setInputs(
        clearHiddenReportParameterValues(buildParameterInputList(cfg, keys)),
      )
      setLoading(false)
      return cfg
    }

    const pdfReportResource = getPdfReportResource(cfg.printData)
    if (pdfReportResource) {
      if (fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl)
      }
      setDisplayType('pdfAttachment')
      setFileUrl(getPdfReportFileUrl(pdfReportResource))
      setBlobFile(null)
      setTotalPage(1)
      setTotalInPageIndex({ 0: 1 })
      setBoxes(cfg.printData || {})
      setLoading(false)
      return cfg
    }

    if (fileUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(fileUrl)
    }
    setDisplayType('web')
    setFileUrl(null)
    setBlobFile(null)
    setPdfPrintSignal(0)

    let newTotalPage = 1
    let newTotalInPage = { 0: 1 }
    if (cfg.layoutData?.pages && cfg.layoutData.pages > 1) {
      newTotalPage = cfg.layoutData.pages
      for (let i = 0; i < cfg.layoutData.pages; i++) {
        newTotalInPage[i] = 1
      }
    }

    const list = buildParameterInputList(cfg, keys)
    let dsData = { _table: {}, _dsName: {}, _columns: {} }

    cfg.dataSourceData?.list?.forEach((element) => {
      element.fieldVariable?.value?.forEach((vitem) => {
        dsData = Object.assign(dsData, vitem)
      })
      dsData._table[element.dataSourceId] = element.fieldVariable?.value
      dsData._columns[element.dataSourceId] = element.fieldVariable?.columns
      dsData._dsName[element.name] = element.fieldVariable?.value
    })

    const inputParam = list
    setInputs(clearHiddenReportParameterValues(list))

    if (cfg.printData) {
      let syFormResult

      Object.keys(cfg.printData).map((key, index) => {
        const bindValue = getReportBindValue(dsData, cfg.printData[key].name)
        if (typeof bindValue !== 'undefined') {
          cfg.printData[key].value = bindValue
          if (cfg.printData[key].name === 'syformresult') {
            syFormResult = cfg.printData[key].value
            delete cfg.printData[key]
          }
        } else if (
          dsData[cfg.printData[key].name] === undefined &&
          cfg.printData[key].type === 'object'
        ) {
          cfg.printData[key].value = ''
        }
      })

      let objectOrderList = []
      await Promise.all(Object.keys(cfg.printData).map(async (key, index) => {
        if (syFormResult && syFormResult[cfg.printData[key].value?.name]) {
          if (syFormResult[cfg.printData[key].value.name + '-Comment']) {
            cfg.printData[key].comment =
              syFormResult[cfg.printData[key].value.name + '-Comment']
          }

          // type = 'radiogroup'
          cfg.printData[key].type = cfg.printData[key].value.type
          cfg.printData[key].choices = cfg.printData[key].value.choices
          cfg.printData[key].value = syFormResult[cfg.printData[key].value.name]
        } else if (
          cfg.printData[key].value instanceof Object &&
          !(cfg.printData[key].value instanceof Array)
        ) {
          // need syFormResult
          cfg.printData[key].value = cfg.printData[key].value.title
        }

        if (
          (cfg.printData[key].type == 'table' ||
            cfg.printData[key].type == 'advance_table') &&
          cfg.printData[key].dataSourceId &&
          cfg.printData[key].dataSourceId > 0
        ) {
          cfg.printData[key].type = 'advance_table_result'
          cfg.printData[key].value =
            dsData._table[cfg.printData[key].dataSourceId]

          // --- format date ---
          let printValue = cfg.printData[key]?.value
          printValue?.forEach((valueObj) => {
            for (const key in valueObj) {
              if (isIsoDate(valueObj[key])) {
                valueObj[key] = moment(valueObj[key])
                  .add(543, 'year')
                  .format('D MMMM YYYY')
              }
            }
          })
          // --- format date ---

          //console.log('len', cfg.printData[key].value)
          if(cfg.printData[key]?.renderUI ==='GROUP_LEVEL'){
            //Count Group Level for row
            //TODO : count for in-case 2 pages design 
            const groupPages = cfg.printData[key].pageBreakGroupBy
              ? paginateReportGroups(cfg.printData[key])
              : null
            if (groupPages) cfg.printData[key]._groupPages = groupPages
            const meta = groupPages
              ? { totalPage: Math.max(groupPages.length, 1), totalInPage: { 0: Math.max(groupPages.length, 1) } }
              : calculateVirtualRowTotalPage(cfg,cfg.printData[key])
            newTotalPage = meta.totalPage
            newTotalInPage = meta.totalInPage
          }else if (cfg.printData[key].rowPerPage) {
            const total = calculateReportRepeatTotalPage({
              rowCount: cfg.printData[key]?.value?.length || 0,
              rowPerPage: cfg.printData[key].rowPerPage,
              pageBreakAfterRows: cfg.printData[key].pageBreakAfterRows,
              lastPageRowPerPage: cfg.printData[key].lastPageRowPerPage,
              rowsOnFirstPageOnly:
                cfg.printData[key].rowsOnFirstPageOnly === true,
            })

            if (total > 1) {
              if (cfg.layoutData?.pages && cfg.layoutData.pages > 1) {
                newTotalPage += total - 1
                newTotalInPage[cfg.printData[key].page - 1] = total
              } else {
                newTotalPage = total
                newTotalInPage = { 0: total }
              }
            }
          }
          if (
            cfg.printData[key].dynamicResult &&
            cfg.printData[key].dynamicResult === true
          ) {
            objectOrderList.push(cfg.printData[key])
          }
        } else if (
          cfg.printData[key].type === 'texteditor' &&
          cfg.printData[key].rowPerPage
        ) {
          const total = calculateTextEditorRepeatTotalPage(
            cfg.printData[key],
            cfg.dataSourceData?.list || [],
          )

          if (total > 1) {
            if (cfg.layoutData?.pages && cfg.layoutData.pages > 1) {
              newTotalPage += total - 1
              newTotalInPage[cfg.printData[key].page - 1] = total
            } else {
              newTotalPage = total
              newTotalInPage = { 0: total }
            }
          }
        } else if (cfg.printData[key].type == 'choice') {
          if (dsData._dsName[cfg.printData[key].name])
            cfg.printData[key].value = dsData._dsName[cfg.printData[key].name]
        } else if (
          cfg.printData[key].type == 'object' &&
          isIsoDate(cfg.printData[key])
        ) {
          cfg.printData[key].type = 'object_date'
          cfg.printData[key].format = 'D MMMM YYYY'
        } else if (cfg.printData[key].type === 'search_param') {
          let param = cfg.printData[key].name.replace(/[{}]/g, '')
          inputParam.forEach((x) => {
            if (x.field === param) cfg.printData[key].value = x.value
          })
        } else if (cfg.printData[key].type === 'get_api') {
          let obj = await getFromApi(cfg.printData[key].endpoint)
          cfg.printData[key].type = 'text'
          cfg.printData[key].value = dot.pick(cfg.printData[key].name, obj)
        } else if (cfg.printData[key].type === 'vital_sign') {
          cfg.printData[key].propsList = []
          if (cfg.printData[key]?.dataSourceId) {
            cfg.printData[key].propsList =
              dsData._table[cfg.printData[key].dataSourceId]
          }
        }

        cfg.printData[key].keyIndex = key
        return cfg.printData[key]
      }))

      if (objectOrderList.length > 0) {
        //Reorder
        // Sort the array based on the 'top' property
        let objectArray = Object.keys(cfg.printData).map((key, index) => {
          return cfg.printData[key]
        })
        objectArray.sort((a, b) => a.top - b.top)
        console.log(...objectArray)
        objectArray.map((item, index) => {
          if (item.top > objectOrderList[0].top) {
            let lastItem = objectOrderList[objectOrderList.length - 1]
            if (lastItem.type === 'advance_table_result') {
              item.top =
                lastItem.top +
                lastItem.value.length * parseInt(lastItem.rowHeight)
              ///cfg.printData[item.keyIndex] = item
              objectOrderList.push(item)
            } else {
            }
          }
        })
        ///Calculate Start
      }
      setTotalPage(newTotalPage)
      setTotalInPageIndex(newTotalInPage)

      console.log(objectOrderList)
      setBoxes(cfg.printData)

      //console.log('ds',cfg.printData)
    }

    return cfg
  }

  const calculateVirtualRowTotalPage = (cfg,item)=>{
    let prevGroupValues = {
        level1: null,
        level2: null,
        level3: null
      }

      let totalVisualRow = 0

      item.value.forEach((ritem) => {

        item.columns.forEach((citem) => {

          const itemProps = item.fieldVariable?.props?.[citem]

          if (itemProps?.groupDisplay === true) {

            const level = itemProps.groupLevel || 'level1'
            const currentValue = ritem[citem]

            if (prevGroupValues[level] !== currentValue) {

              // reset lower
              if (level === 'level1') {
                prevGroupValues.level2 = null
                prevGroupValues.level3 = null
              }

              if (level === 'level2') {
                prevGroupValues.level3 = null
              }

              prevGroupValues[level] = currentValue

              totalVisualRow++  // group row
            }
          }

          //console.log('citem',citem,ritem)
        })

        totalVisualRow++ // detail row
      })


      
  
      const totalPage = item.rowPerPage
        ? Math.ceil(totalVisualRow / item.rowPerPage)
        : 1;
      
      return {totalPage,totalInPage:{0:totalPage}}
             
      //return totalPage;
  }

  const getInitReport = () => {
    return {
      reportName: '',
      publishData: {
        printStyle: false,
        pdfStickerStyle: false,
        rotateOnPrint: false,
        autoPrint: false,
        exportExcel: false,
      },
      dataSourceData: { list: [] },
    }
  }

  const browserPrint = () => {
    setPrintDocumentTitle(report)

    if (displayType === 'pdfAttachment' && fileUrl) {
      setPdfPrintSignal((current) => current + 1)
      return
    }

    if (report?.publishData?.pdfStickerStyle && pdfSrc) {
      printPdfBlobUrl(pdfSrc)
      return
    }

    // Reports embedded in Patient File must print their isolated report page.
    // Flow Print and Save as PDF use this same path after pagination is ready.
    if (!showOnlyPrintArea && props?.printOnNewTab) {
      if (props.printOnNewTab.openNewTab) {
        window.open(
          buildPrintReportUrl({
            autoPrint: 'false',
            printIntent: 'preview',
            showOnlyPrintArea: 'true',
            browserPrint: props.printOnNewTab.autoPrint ? 'true' : 'false',
          }),
          '_blank',
        )
      } else {
        printWebReportInFrame()
      }
      return
    }

    window.print()
  }

  const onExportExcel = async () => {
    const reportTemplateId = Number(
      report?.reportTemplateId ||
        query?.reportTemplateId ||
        props?.reportTemplateId,
    )

    if (!reportTemplateId) {
      await Swal.fire({
        icon: 'error',
        title: 'ดาวน์โหลด Excel ไม่สำเร็จ',
        text: 'ไม่พบ reportTemplateId',
      })
      return
    }

    const normalizedInputs = clearHiddenReportParameterValues(inputs)
    const missingInputs = normalizedInputs.filter(
      (item) =>
        isReportParameterRequired(item, normalizedInputs) &&
        item?.dropdownData?.source !== 'datasource' &&
        !hasReportParamValue(item?.value),
    )
    if (missingInputs.length) {
      await Swal.fire({
        icon: 'error',
        title: 'ข้อมูลไม่ครบ',
        text:
          'กรุณาระบุ ' +
          missingInputs.map((item) => item.title || item.field).join(', '),
      })
      return
    }

    const params = normalizedInputs.reduce((result, item) => {
      if (!isReportParameterVisible(item, normalizedInputs)) return result

      const key = normalizeReportParamName(item?.field || item?.title)
      if (!key || !hasReportParamValue(item?.value)) return result

      result[key] = normalizeReportDateParameter({ ...item, field: key })
      return result
    }, {})

    const excelProfile = report?.publishData?.excelProfile || {}
    const hasExcelProfile =
      excelProfile.dataSourceId ||
      Object.keys(excelProfile.dataSources || {}).length > 0

    setIsExportingExcel(true)
    try {
      const response = await httpClient.post(
        `/report/report-excel/export/${reportTemplateId}`,
        {
          params,
          mode: hasExcelProfile ? 'profile' : 'layout',
        },
        {
          responseType: 'blob',
        },
      )
      const contentType =
        response?.headers?.['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const fileName = getExcelDownloadFileName(
        response,
        getPrintDocumentTitle(report),
      )
      FileSaver.saveAs(
        new Blob([response.data], { type: contentType }),
        fileName,
      )
    } catch (error) {
      console.error('Unable to export report Excel', error)
      await Swal.fire({
        icon: 'error',
        title: 'ดาวน์โหลด Excel ไม่สำเร็จ',
        text: 'ไม่สามารถสร้างไฟล์ Excel ได้ กรุณาลองใหม่อีกครั้ง',
      })
    } finally {
      setIsExportingExcel(false)
    }
  }

  const getAllParamInput = (value) => {
    let param = ''
    let keys = { ...query }
    const normalizedValue = clearHiddenReportParameterValues(value)

    let failed = []
    normalizedValue.forEach((item, index) => {
      //param += '&'+item.field+'='+item.value;
      const isVisible = isReportParameterVisible(item, normalizedValue)
      keys[item.field] = isVisible ? normalizeReportDateParameter(item) : ''
      if (!isVisible || !isReportParameterRequired(item, normalizedValue)) {
        return
      }
      if (item?.dropdownData?.source === 'datasource') {
        return
      }
      if (!item.value || item.value === null || item.value === '') {
        failed.push(item.title)
      }
    })
    if (failed.length > 0) {
      withReactContent(Swal).fire({
        icon: 'error',
        title: 'Error',
        text: 'ข้อมูล ' + failed.join(', ') + ' ว่างอยู่ กรุณาใส่ข้อมูล ',
      })
      return { keys: null, param: null }
    }

    Object.keys(keys).map((key, index) => {
      param += '&' + key + '=' + keys[key]
    })

    keys = Object.assign(query, keys)
    setInputs(normalizedValue)
    setQuery(keys)
    setInputParam(param)
    return { param, keys }
  }

  const onSearch = async (value) => {
    //console.log("onsearch",value)
    //setInputs([].concat(value))
    //Reset

    //Validate

    let { keys, param } = getAllParamInput(value)
    if (param && keys) {
      setReportInputsReady(false)
      setTotalPage(0)
      if (isPdfOverlayOutputMode(report?.publishData)) {
        await generatePdfOverlay(param)
        setReportInputsReady(true)
      } else if (
        report?.publishData?.pdfStickerStyle &&
        report?.publishData?.pdfStickerStyle === true
      ) {
        await generatePdf(param, keys)
        setReportInputsReady(true)
      } else if (displayType == 'pdf') {
        //Do Something
      } else {
        let cfg = await searchByInput(param, keys)
      }
    }
  }

  const onChange = (index, value) => {
    const dateValue = (items, field) => items.find((item) => item.field === field)?.value || ''
    const changedFields = ['startDate', 'endDate'].filter(
      (field) => dateValue(inputs, field) !== dateValue(value, field),
    )
    const dependentInputs = value.filter((item) =>
      item.dropdownData?.source === 'datasource' &&
      item.dropdownData?.dependsOn?.some((field) => changedFields.includes(field)),
    )
    if (!dependentInputs.length) {
      setInputs([].concat(value))
      return
    }

    const sourceIds = dependentInputs.map((item) => item.dropdownData.dataSourceId)
    const nextInputs = value.map((item) =>
      sourceIds.includes(item.dropdownData?.dataSourceId)
        ? { ...item, value: '', dropdownData: { ...item.dropdownData, choices: [] } }
        : item,
    )
    setInputs(nextInputs)
    clearTimeout(dropdownOptionsTimerRef.current)
    dropdownOptionsRequestRef.current += 1
    const requestId = dropdownOptionsRequestRef.current
    const startDate = dateValue(nextInputs, 'startDate')
    const endDate = dateValue(nextInputs, 'endDate')
    setLoadingDataSourceIds([])
    if (!moment(startDate).isValid() || !moment(endDate).isValid()) return

    const from = moment(startDate).format('YYYY-MM-DD')
    const to = moment(endDate).format('YYYY-MM-DD')
    if (from > to || !report?.reportTemplateId) return

    setLoadingDataSourceIds(sourceIds)
    dropdownOptionsTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await httpClient.get(
          `/report/report-webview/find?convertQuery=true&reportTemplateId=${report.reportTemplateId}&startDate=${encodeURIComponent(from)}&endDate=${encodeURIComponent(to)}`,
        )
        if (requestId !== dropdownOptionsRequestRef.current) return
        const dataSources = data?.data?.dataSourceData?.list || []
        setInputs((current) => current.map((item) => {
          const dataSourceId = item.dropdownData?.dataSourceId
          if (!sourceIds.includes(dataSourceId)) return item
          const choices = dataSources.find(
            (source) => source.dataSourceId === dataSourceId,
          )?.fieldVariable?.value || []
          return { ...item, dropdownData: { ...item.dropdownData, choices } }
        }))
      } catch (error) {
        console.error('Unable to load dropdown options for selected dates', error)
      } finally {
        if (requestId === dropdownOptionsRequestRef.current) {
          setLoadingDataSourceIds([])
        }
      }
    }, 300)
  }

  const renderTableItem = (item) => {
    if (item.type == 'table') {
      const header = (
        <tr>
          {item.columns ? (
            item.columns.map((citem, index) => <td>{citem}</td>)
          ) : (
            <td></td>
          )}
        </tr>
      )

      const rowsData =
        item.value && Array.isArray(item.value) ? (
          item.value.map((ritem, rindex) => (
            <tr key={rindex}>
              {item.columns.map((citem, index) => (
                <td key={index}>{ritem[citem]}</td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            {new Array(0).fill('').map((item, cindex) => (
              <td></td>
            ))}
          </tr>
        )

      return [header, rowsData]
    } else if (item.type == 'advance_table_result') {
      return renderAdvanceTable(item)
    }
  }

  const renderAdvanceTable = (item) => {
    const header = (
      <tr>
        {item.columns ? (
          item.columns.map((citem, index) => <td key={index}>{citem}</td>)
        ) : (
          <td></td>
        )}
      </tr>
    )
    const rowsData = item.value.map((ritem, rindex) => {
      return (
        <tr key={rindex}>
          {item.columns.map((citem, index) => {
            return <td key={index}>{ritem[citem]}</td>
          })}
        </tr>
      )
    })
    return [header, rowsData]
  }

  const renderExportExcel = () => {
    let style = { display: 'none' }
    if (debug) {
      style = { display: '', background: 'white' }
    }
    return (
      <div style={style}>
        <table id="export-area">
          <thead>
            <tr>
              <td>ชื่อรายงาน</td>
              <td>{report.reportName}</td>
            </tr>
          </thead>
          <tbody>
            {Object.keys(boxes).map((key) => {
              const item = boxes[key]

              return renderTableItem(item)
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const renderOldLayout = () => {
    return (
      <>
        {new Array(totalPage).fill('').map((item, index) => {
          if (layout.pages > 1) {
            {
              return new Array(layout.pages).fill('').map((x, pageIndex) => (
                <div
                  // style={{
                  //   margin: autoPrint
                  //     ? 'inherit'
                  //     : layout.orientation === 'landscape'
                  //     ? `calc(${layout.width} - ${layout.height} + 60px)`
                  //     : '',
                  // }}
                  key={pageIndex}
                  className={
                    report.publishData.rotateOnPrint ? ' print-rotated' : ''
                  }
                >
                  <PrintArea
                    key={pageIndex}
                    boxes={Object.filter(
                      boxes,
                      (item) =>
                        isReportItemVisibleOnLayoutPage(item, pageIndex + 1),
                    )}
                    pageNo={index + 1}
                    totalPage={totalPage}
                    layout={layout}
                    dataSourceList={report.dataSourceData?.list || []}
                    reportParams={query}
                    showValue
                    disabled
                  />
                </div>
              ))
            }
          } else {
            return (
              <div
                // style={{
                //   margin: autoPrint
                //     ? 'inherit'
                //     : layout.orientation === 'landscape'
                //     ? `calc(${layout.width} - ${layout.height} + 60px)`
                //     : '',
                // }}
                className={
                  report.publishData.rotateOnPrint ? ' print-rotated' : ''
                }
              >
                <PrintArea
                  boxes={boxes}
                  pageNo={index + 1}
                  totalPage={totalPage}
                  layout={layout}
                  dataSourceList={report.dataSourceData?.list || []}
                  reportParams={query}
                  showValue
                  disabled
                />
              </div>
            )
          }
        })}
      </>
    )
  }

  const renderPdf = () => {
    if (isLoading) {
      return (
        <div>
          <p>Loading... waiting {seconds} seconds...</p>
        </div>
      )
    }
    return (
      <PreviewFile fileUrl={fileUrl} heightPadding={'140px'} />
      //onDownloadClick={onSavePdf} onPrintClick={onSavePdf} onSaveClick={onSavePdf}/>
    )
  }

  const renderPdfAttachment = () => {
    if (!fileUrl) {
      const pdfOverlayMode = isPdfOverlayOutputMode(report?.publishData)
      const previewMessage = pdfOverlayMode
        ? pdfOverlayPreviewStatus === 'error'
          ? 'ไม่สามารถสร้างตัวอย่าง PDF Overlay ได้ กรุณาลองใหม่อีกครั้ง'
          : 'กำลังสร้างตัวอย่าง PDF Overlay กรุณารอสักครู่...'
        : 'ไม่พบไฟล์ PDF สำหรับรายงานนี้'

      return (
        <div className="d-flex align-items-center justify-content-center text-white p-3">
          {pdfOverlayMode && pdfOverlayPreviewStatus !== 'error' && (
            <Spinner animation="border" size="sm" className="mr-2" />
          )}
          <span>{previewMessage}</span>
        </div>
      )
    }

    return (
      <PreviewFile
        fileUrl={fileUrl}
        heightPadding={'140px'}
        printSignal={pdfPrintSignal}
      />
    )
  }

  const renderNewLayout = () => {
    if (flowEnabled) return <FlowPrintLayout boxes={boxes} layout={layout}
      dataSourceList={report.dataSourceData?.list || []} reportParams={query} onStatus={onFlowStatus} />
    if (layout.pages > 1) {
      let pageNo = 0
      return new Array(layout.pages).fill('').map((x, pageIndex) => {
        return new Array(totalInPageIndex[pageIndex])
          .fill('')
          .map((inPageItem, index) => (
            <div
              key={'page-' + (pageIndex + 1) + '-repeat-' + (index + 1)}
              className={
                'print-page-sheet' +
                (pageNo + 1 === totalPage ? ' last-print-page-sheet' : '') +
                (report.publishData.rotateOnPrint ? ' print-rotated' : '')
              }
            >
              <PrintArea
                boxes={Object.filter(
                  boxes,
                  (item) =>
                    isReportItemVisibleOnLayoutPage(item, pageIndex + 1),
                )}
                pageNo={++pageNo}
                pageNoOfLayoutPage={index + 1}
                totalPage={totalPage}
                totalPageOfLayoutPage={totalInPageIndex[pageIndex]}
                layout={layout}
                dataSourceList={report.dataSourceData?.list || []}
                reportParams={query}
                showValue
                disabled
              />
            </div>
          ))
      })
    } else {
      return new Array(totalPage).fill('').map((item, index) => {
        return (
          <div
            key={'page-' + (index + 1)}
            className={
              'print-page-sheet' +
              (index + 1 === totalPage ? ' last-print-page-sheet' : '') +
              (report.publishData.rotateOnPrint ? ' print-rotated' : '')
            }
          >
            <PrintArea
              boxes={boxes}
              pageNo={index + 1}
              pageNoOfLayoutPage={index + 1}
              totalPage={totalPage}
              totalPageOfLayoutPage={totalPage}
              layout={layout}
              dataSourceList={report.dataSourceData?.list || []}
              reportParams={query}
              showValue
              disabled
            />
          </div>
        )
      })
    }
  }

  const cleanInputParam = () => {
    const params = new URLSearchParams(inputParam);

    // Remove this key if exists
    params.delete("reportTemplateId");

    return params.toString() ? `&${params.toString()}` : "";
  };

  const switchLang = ()=>{
      const lang = report?.publishData?.languageSupport;

      const reportCode = report?.publishData?.langReportCode?.[lang] || null;
      if(reportCode){
        const query = cleanInputParam();
        let url = `/#/print/report?reportCode=${reportCode}${query}`
        window.open(url, '_blank')    
      }else{
        Swal.fire({
          title: 'ไม่พบรายงาน',
          icon: 'error',
          text: `ไม่พบรายงานในภาษานี้`,
          confirmButtonText: 'OK',
        })        
      }
  }

  const renderSelectLang = ()=>{
     
    return  (
      <Button
          variant="outline-default"
          className="m-r-10"
          onClick={() => {
            setIsLoading(true)
            switchLang()
          }}
        >
          
          <span style={{ fontSize: 24 }}>{report.publishData.languageSupport==='th'?'🇹🇭':'🇬🇧'}</span>
        </Button>
    )
  }

  const desktopPdfBlob = async () => {
    const source = pickDesktopPrintSource({ displayType, fileUrl,
      pdfStickerStyle: report?.publishData?.pdfStickerStyle, pdfSrc, hasHeldBlob: Boolean(blobFile) })
    if (source.kind === 'unavailable') throw new Error('เอกสารยังไม่พร้อม กรุณาค้นหาหรือสร้างตัวอย่างก่อนพิมพ์')
    if (source.kind === 'held') return blobFile
    if (source.url) {
      if (source.url.startsWith('blob:')) {
        const response = await fetch(source.url)
        if (!response.ok) throw new Error('อ่านเอกสารไม่สำเร็จ')
        return response.blob()
      }
      const response = await httpClient({ method: 'get', url: source.url, responseType: 'blob' })
      return response.data
    }
    let appURL = `${window.location.protocol}//${window.location.host}`
    if (process.env.NODE_ENV === 'development') appURL = 'https://dev.ckd.ckhis.xyz'
    const response = await httpClient({ method: 'post', url: '/report/report-pdf/export-pdf',
      data: { url: `${appURL}/#/print/report?${reportPdfRenderQuery(inputParam)}` }, responseType: 'blob' })
    if (!response?.data?.size) throw new Error('สร้าง PDF ไม่สำเร็จ')
    return new Blob([response.data], { type: 'application/pdf' })
  }

  const renderPrintStyleButton = () => {
    const content = [
      <Button
        key="print-report"
        variant="outline-primary"
        className="m-r-10"
        disabled={isPrintPreparing || (flowEnabled && !flowStatus.ready)}
        title="พิมพ์รายงานผ่าน Browser"
        onClick={() => {
          if (flowEnabled) { browserPrint(); return }
          if (
            displayType === 'pdfAttachment' ||
            report?.publishData?.pdfStickerStyle
          ) {
            browserPrint()
          } else if (props?.onClickPrint) {
            props.onClickPrint()
          } else {
            browserPrint()
          }
        }}
      >
        {isPrintPreparing ? (
          <Spinner animation="border" size="sm" className="vx-icon" />
        ) : (
          <FeatherIcon size="16" icon="printer" className="vx-icon" />
        )}
        {(displayType === 'pdfAttachment' ||
          report?.publishData?.pdfStickerStyle) && (
          <span className="ml-1">Print</span>
        )}
      </Button>,
    ]
    if (displayType === 'pdfAttachment') {
      return content
    }

    if (displayType == 'web') {
      content.push(
        <Button
          key="preview-pdf"
          variant="outline-danger"
          className="m-r-10"
          title={flowEnabled ? 'บันทึก PDF จากหน้าที่จัดแล้ว: เลือก Save as PDF ในหน้าพิมพ์' : 'แสดง PDF'}
          disabled={flowEnabled && !flowStatus.ready}
          onClick={() => {
            if (flowEnabled) { browserPrint(); return }
            setIsLoading(true)
            setDisplayType('pdf')
          }}
        >
          <Card.Img
            variant="top"
            src={PdfIcon}
            alt={'PDF'}
            style={{ width: '24px', height: '24px' }}
          />
        </Button>,
      )
    } else {
      content.push(
        <Button
          key="preview-web"
          variant="outline-secondary"
          className="m-r-10"
          onClick={() => {
            setDisplayType('web')
          }}
        >
          <FeatherIcon size="16" icon="file" className="vx-icon" />
        </Button>,
      )
    }
    return content
  }


  // filter page
  Object.filter = (obj, predicate) =>
    Object.keys(obj)
      .filter((key) => predicate(obj[key]))
      .reduce((res, key) => Object.assign(res, { [key]: obj[key] }), {})

  const autoPrint = showOnlyPrintArea
  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className="print-report"
        data-report-ready={String(initialReportReady && reportInputsReady && Boolean(report.reportTemplateId || props?.jsonData) && !loading && !isLoading &&
          (!flowEnabled || flowStatus.ready) &&
          (displayType !== 'pdfAttachment' || Boolean(fileUrl)) &&
          (!report.publishData.pdfStickerStyle || Boolean(pdfSrc)))}
        style={{ width: '100%', background: '#525659' }}
      >
        {isPrintPreparing && (
          <div className="print-preparing">
            <div className="print-preparing__box">
              <Spinner animation="border" size="sm" />
              <span>กำลังเตรียมหน้าพิมพ์...</span>
            </div>
          </div>
        )}
        <div
          className={'header-page '}
          style={{
            padding: '10px',
            background: 'white',
            display: autoPrint ? 'none' : '',
          }}
        >
          {flowEnabled && <span data-flow-ready={String(flowStatus.ready)} />}
          <Row>
            <div style={{ width: '960px', margin: '0 auto' }}>
              <div style={{ float: 'left' }}>
                ชื่อรายงาน {report.reportName} จำนวน {flowEnabled && !flowStatus.ready ? 'กำลังจัด' : totalPage} หน้า
              </div>
              <div style={{ float: 'right',display:'inline-flex' }}>
                {report.publishData.languageSupport && renderSelectLang()}
                {(report.publishData.printStyle ||
                  report.publishData.pdfStickerStyle ||
                  displayType === 'pdfAttachment') &&
                  renderPrintStyleButton()}
                {report.publishData.exportExcel && (
                  <Button
                    color="success"
                    className="m-r-1"
                    title="ดาวน์โหลด Excel"
                    aria-label="ดาวน์โหลด Excel"
                    disabled={isExportingExcel}
                    onClick={onExportExcel}
                  >
                    {isExportingExcel ? (
                      <Spinner animation="border" size="sm" className="vx-icon" />
                    ) : (
                      <FeatherIcon
                        size="16"
                        icon="download"
                        className="vx-icon"
                      />
                    )}
                  </Button>
                )}
                {!showOnlyPrintArea && <ReportPrintSettings
                  ref={desktopPrintRef}
                  reportCode={report.reportCode || report.reportTemplate?.code || query.reportCode}
                  device={report.desktopPrintDevice}
                  printConfig={report.desktopPrintConfig}
                  getPdfBlob={desktopPdfBlob}
                  paperSize={['A4', 'A5', 'Letter', '80mm', '58mm'].includes(layout.size)
                    ? layout.size : 'auto'}
                  disabled={!report.reportTemplateId || loading || isLoading || (flowEnabled && !flowStatus.ready) ||
                    (displayType === 'pdfAttachment' && !fileUrl) ||
                    (report.publishData.pdfStickerStyle && !pdfSrc)}
                />}
              </div>
            </div>
          </Row>
          <HeaderArea
            value={inputs}
            onChange={(index, value) => onChange(index, value)}
            onSearch={(e) => onSearch(e)}
            showSearch={
              displayType !== 'pdfAttachment' ||
              isPdfOverlayOutputMode(report?.publishData)
            }
            showInput={props?.showInput}
            loading={loading}
            loadingDataSourceIds={loadingDataSourceIds}
          />
        </div>
        {report?.publishData?.pdfStickerStyle &&
         report?.publishData?.pdfStickerStyle === true ? (
          pdfSrc && <embed src={pdfSrc} width="100%" height={`${height}px`} />
        ) : (
          <div ref={printPageRef} className={'print-page ' + (autoPrint ? 'auto-print' : '')}>
            {displayType === 'web' && renderNewLayout()}
            {displayType === 'pdf' && renderPdf()}
            {displayType === 'pdfAttachment' && renderPdfAttachment()}

            {report.publishData.exportExcel && renderExportExcel()}
          </div>
        )}
        <div className="footer-page">
          <div className={'footer-content text-center'}>
            Copyright © 2022-{new Date().getFullYear()} CK-Doctor All rights
            reserved.
          </div>
        </div>
      </div>
    </DndProvider>
  )
}

export default PrintReport
